import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { Plus, Pencil, Trash2, Loader2, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Progress } from '@/components/ui/progress'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { usePages, pageSchema, type Page } from '@/services/use-pages'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { ConfirmDialog } from '@/components/confirm-dialog'

export const Route = createFileRoute('/_authenticated/sayfalar/')({
  component: SayfalarPage,
})

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
  { title: 'Kurumsal İçerik Sayfaları', href: '/sayfalar', isActive: true },
]

const LANGUAGES = [
  { code: 'tr', label: 'Türkçe', required: true },
  { code: 'en', label: 'İngilizce' },
  { code: 'fr', label: 'Fransızca' },
  { code: 'es', label: 'İspanyolca' },
  { code: 'ru', label: 'Rusça' },
  { code: 'nl', label: 'Felemenkçe' },
  { code: 'ar', label: 'Arapça' },
]

function SayfalarPage() {
  const { pages, isLoading, isCreating, isUpdating, isDeleting, createPage, updatePage, deletePage } = usePages()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingPage, setEditingPage] = useState<Page | null>(null)
  const [activeTab, setActiveTab] = useState('tr')

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [pageToDelete, setPageToDelete] = useState<string | null>(null)

  // AI Translation State
  const [isAISaving, setIsAISaving] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState('')

  const form = useForm<Page>({
    resolver: zodResolver(pageSchema),
    defaultValues: {
      slug: '',
      title: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
      content: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
      isActive: true,
      translate: false,
    },
  })

  useEffect(() => {
    let interval: NodeJS.Timeout
    if (showProgress && progress < 100) {
      interval = setInterval(() => {
        setProgress(p => {
          if (p >= 90) return 90 // wait at 90% until backend returns
          return p + Math.floor(Math.random() * 15)
        })
      }, 800)
    }
    return () => clearInterval(interval)
  }, [showProgress, progress])

  const openNewDialog = () => {
    form.reset({
      slug: '',
      title: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
      content: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
      isActive: true,
      translate: false,
    })
    setEditingPage(null)
    setActiveTab('tr')
    setIsDialogOpen(true)
  }

  const openEditDialog = (page: Page) => {
    form.reset({
      _id: page._id,
      slug: page.slug,
      title: { tr: page.title.tr, en: page.title.en || '', fr: page.title.fr || '', es: page.title.es || '', ru: page.title.ru || '', nl: page.title.nl || '', ar: page.title.ar || '' },
      content: { tr: page.content.tr, en: page.content.en || '', fr: page.content.fr || '', es: page.content.es || '', ru: page.content.ru || '', nl: page.content.nl || '', ar: page.content.ar || '' },
      isActive: page.isActive,
      translate: false,
    })
    setEditingPage(page)
    setActiveTab('tr')
    setIsDialogOpen(true)
  }

  const onSubmit = async (values: Page) => {
    try {
      if (editingPage && editingPage._id) {
        updatePage({ id: editingPage._id, data: values })
      } else {
        createPage(values)
      }
      setIsDialogOpen(false)
    } catch (error) {
      console.error(error)
    }
  }

  const onTranslateAndSave = async () => {
    const isValid = await form.trigger()
    if (!isValid) return

    const values = form.getValues()
    values.translate = true // set AI flag

    setIsAISaving(true)
    setShowProgress(true)
    setProgress(0)
    setCurrentStep('AI Modeli başlatılıyor...')

    setTimeout(() => setCurrentStep('Türkçe metin analiz ediliyor...'), 1000)
    setTimeout(() => setCurrentStep('6 Kıtaya uygun diller çeviriliyor...'), 2500)
    setTimeout(() => setCurrentStep('HTML ve Format korunuyor...'), 5000)

    try {
      if (editingPage && editingPage._id) {
        updatePage({ id: editingPage._id, data: values })
      } else {
        createPage(values)
      }
      setProgress(100)
      setCurrentStep('İşlem başarıyla tamamlandı!')
      setTimeout(() => {
        setIsDialogOpen(false)
        setShowProgress(false)
        setIsAISaving(false)
      }, 1000)
    } catch (error) {
      console.error(error)
      setShowProgress(false)
      setIsAISaving(false)
    }
  }

  const handleDeleteClick = (id: string) => {
    setPageToDelete(id)
    setIsDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (pageToDelete) {
      try {
        await deletePage(pageToDelete)
        setIsDeleteDialogOpen(false)
        setPageToDelete(null)
      } catch (error) {
        console.error(error)
      }
    }
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">İçerik Sayfaları</h1>
            <p className="text-muted-foreground text-sm">KVKK, Gizlilik Politikası, İletişim Metni gibi dinamik sayfaları yönetin.</p>
          </div>
          <Button onClick={openNewDialog}>
            <Plus className="mr-2 h-4 w-4" /> Yeni Sayfa Ekle
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Mevcut Sayfalar</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
            ) : pages.length === 0 ? (
              <div className="text-center p-8 text-muted-foreground">Henüz sayfa eklenmemiş.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Slug (Uzantı)</TableHead>
                    <TableHead>Başlık (TR)</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead className="text-right">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pages.map((page) => (
                    <TableRow key={page._id}>
                      <TableCell className="font-medium">
                        <Badge variant="outline">/{page.slug}</Badge>
                      </TableCell>
                      <TableCell>{page.title?.tr}</TableCell>
                      <TableCell>
                        <Badge variant={page.isActive ? 'default' : 'secondary'}>
                          {page.isActive ? 'Aktif' : 'Pasif'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="outline" size="icon" onClick={() => openEditDialog(page)}>
                          <Pencil className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            if (page._id) handleDeleteClick(page._id)
                          }}
                        >
                          {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-red-500" />}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Form Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPage ? 'Sayfayı Düzenle' : 'Yeni Sayfa'}</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="slug"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Slug (Uzantı)</FormLabel>
                        <FormControl>
                          <Input placeholder="Örn: kvkk, gizlilik-politikasi" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Görünürlük</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full border rounded-md p-4">
                  <TabsList className="w-full flex flex-wrap h-auto mb-4">
                    {LANGUAGES.map((lang) => (
                      <TabsTrigger key={lang.code} value={lang.code} className="flex-1">
                        {lang.label} {lang.required && '*'}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {LANGUAGES.map((lang) => (
                    <TabsContent key={lang.code} value={lang.code} className="space-y-4">
                      <FormField
                        control={form.control}
                        name={`title.${lang.code}` as any}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Sayfa Başlığı ({lang.code.toUpperCase()})</FormLabel>
                            <FormControl>
                              <Input placeholder="Sayfa Başlığını Girin" {...field} value={field.value || ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`content.${lang.code}` as any}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Sayfa İçeriği ({lang.code.toUpperCase()}) - HTML Desteklenir</FormLabel>
                            <FormControl>
                              <Textarea
                                className="min-h-[300px] font-mono text-sm"
                                placeholder="<p>İçeriği buraya yazın...</p>"
                                {...field}
                                value={field.value || ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </TabsContent>
                  ))}
                </Tabs>

                <div className="flex justify-between items-center bg-muted/50 p-4 rounded-lg border">
                  <div className="space-y-1">
                    <h4 className="font-medium flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      Yapay Zeka ile Otomatik Çeviri
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Sadece Türkçe (TR) içeriğini doldurup, diğer dilleri (EN, FR, AR, vb.) yapay zekaya çevirtebilirsiniz. İçeriğinizin HTML tagleri korunur.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="default"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground min-w-[200px]"
                    onClick={onTranslateAndSave}
                    disabled={isAISaving || isCreating || isUpdating}
                  >
                    {isAISaving ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Yapay Zeka Çeviriyor...</>
                    ) : (
                      <><Sparkles className="mr-2 h-4 w-4" /> Yapay Zeka ile Çevir ve Kaydet</>
                    )}
                  </Button>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    İptal
                  </Button>
                  <Button type="submit" disabled={isCreating || isUpdating || isAISaving} variant="secondary">
                    {isCreating || isUpdating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Sadece Kaydet (Çevirisiz)
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Translation Progress Dialog */}
        <Dialog open={showProgress} onOpenChange={setShowProgress}>
          <DialogContent className="sm:max-w-md [&>button]:hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Yapay Zeka Çevirisi
              </DialogTitle>
              <DialogDescription>
                İçerik analiz ediliyor ve 6 farklı dile çevriliyor. HTML tagleri yapay zeka algoritması ile korunuyor.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-muted-foreground">{currentStep}</span>
                  <span className="text-primary">{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
              <div className="text-center text-sm text-muted-foreground animate-pulse">
                Lütfen pencereyi kapatmayın...
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <ConfirmDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          title="Sayfayı Sil"
          desc="Bu sayfayı silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
          destructive
          isLoading={isDeleting}
          handleConfirm={handleConfirmDelete}
        />
      </Main>
    </>
  )
}
